import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../utils/store";
import { Icon } from "../components/tailwind";
import { Button } from "../components/tailwind";
import { UploadSettings, UploadSettingsData } from "./UploadSettings";
import { UploadButton } from "./UploadButton";

type Product = {
  inriver_id: string;
  product_id: string;
  external_id: string | null;
  name: string | null;
  status: "created" | "updated" | "existing";
  inriver_data: any;
};

type FetchResponse = {
  success: boolean;
  products: Product[];
  total: number;
  conflicting_ids: string[];
  failed_ids: string[];
  error?: string;
};

type IdentifierOption = {
  value: string;
  label: string;
  is_default: boolean;
};

type Props = {
  token?: string;
  customerSlug?: string;
};

const getInitialProductIds = (): string => {
  const params = new URLSearchParams(window.location.search);
  const pimIds = params.get("pim_ids");
  return pimIds || "";
};

export const ImportDocumentsFromPim: React.FC<Props> = ({ customerSlug }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [productIds, setProductIds] = useState<string>(getInitialProductIds);
  const [identifierType, setIdentifierType] = useState<string>("sysId");
  const [identifierOptions, setIdentifierOptions] = useState<
    IdentifierOption[]
  >([{ value: "sysId", label: "System ID", is_default: true }]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<FetchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<UploadSettingsData | null>(null);
  const [inriverIntegrationEnabled, setInriverIntegrationEnabled] = useState<
    boolean
  >(false);
  const [loadingFormData, setLoadingFormData] = useState<boolean>(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pimIds = params.get("pim_ids");
    if (pimIds) {
      setProductIds(pimIds);
    }
  }, []);

  // Fetch all form data (including inriver settings) on mount
  useEffect(() => {
    const fetchFormData = async (): Promise<void> => {
      if (!token) return;
      try {
        const response = await fetch(
          `/api/_internal/extract/upload_form_choices`,
          {
            headers: {
              "Content-Type": "application/json",
              token: token || "",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setInriverIntegrationEnabled(
            data.inriver_integration_enabled || false
          );
          setIdentifierOptions(data.identifier_options || []);
          setIdentifierType(data.default_identifier || "sysId");
        } else {
          setError("Could not fetch form data. Please refresh the page.");
        }
      } catch (err) {
        setError("Could not fetch form data. Please refresh the page.");
      } finally {
        setLoadingFormData(false);
      }
    };

    fetchFormData();
  }, [token]);

  const handleSearch = async (): Promise<void> => {
    if (!productIds.trim()) {
      setError("Please enter at least one product ID");
      return;
    }

    if (!token) {
      setError("Authentication required. Please refresh the page.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(
        `/api/_internal/extract/fetch_from_inriver`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token || "",
          },
          body: JSON.stringify({
            product_ids: productIds,
            identifier_type: identifierType,
          }),
        }
      );

      const data: FetchResponse = await response.json();

      if (!response.ok || data.error) {
        setError(data.error || "Failed to fetch products");
        setLoading(false);
        return;
      }

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStart = (): void => {
    setSubmitting(true);
  };

  const handleSubmitError = (error: string): void => {
    alert(error);
    setSubmitting(false);
  };

  // Construct the upload-with-pim URL
  const getUploadWithPimUrl = (): string => {
    // Build the URL using the customer slug
    if (customerSlug) {
      return `/d/${customerSlug}/wizard/upload-with-pim/`;
    }
  };

  const foundCount = result ? result.products.length : 0;
  const notFoundCount = result ? result.failed_ids.length : 0;
  const canSubmit =
    result && result.products.length > 0 && settings && !submitting;

  if (loadingFormData) {
    return (
      <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-flex tw-items-center tw-justify-center tw-p-8">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-space-y-8">
      {/* PIM Search Section */}
      {inriverIntegrationEnabled ? (
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-mb-4">Import from PIM</h1>
          <p className="tw-text-gray-500 tw-mb-6">
            Search for products by entering identifiers separated by commas or
            newlines
          </p>

          {/* Identifier Type Dropdown */}
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Search by
            </label>
            <select
              value={identifierType}
              onChange={(e): void => {
                setIdentifierType(e.target.value);
                setResult(null); // Clear previous results when changing identifier type
                setError(null);
              }}
              className="tw-w-full tw-max-w-xs tw-border tw-border-gray-300 tw-rounded-sm tw-p-2 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 focus:tw-border-transparent"
              disabled={loading}
            >
              {identifierOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="tw-mb-6">
            <textarea
              value={productIds}
              onChange={(e): void => setProductIds(e.target.value)}
              placeholder={`Enter ${
                identifierType === "sysId"
                  ? "System IDs"
                  : `${identifierType} values`
              } separated by commas or newlines (e.g. ${
                identifierType === "sysId"
                  ? "123, 456, 789"
                  : "ABC-123, XYZ-456"
              })`}
              rows={8}
              className="tw-w-full tw-border tw-border-gray-300 tw-rounded-sm tw-p-3 tw-font-mono tw-resize-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 focus:tw-border-transparent"
              disabled={loading}
            />
          </div>

          {result && result.products.length > 0 && (
            <div className="tw-mb-6 tw-flex tw-items-center tw-gap-4 tw-text-sm">
              <span className="tw-font-medium">
                {result.products.length} Total IDs
              </span>
              {result && (
                <>
                  <span className="tw-text-green-600 tw-font-medium">
                    {foundCount} Found
                  </span>
                  <span className="tw-text-red-600 tw-font-medium">
                    {notFoundCount} Not Found
                  </span>
                </>
              )}
            </div>
          )}

          <div className="tw-mb-8">
            <Button
              onClick={handleSearch}
              disabled={loading || !productIds.trim()}
              variant="primary"
              size="lg"
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </div>
        </div>
      ) : (
        <h1 className="tw-text-center tw-my-auto tw-text-gray-500">
          This feature requires an active Inriver integration. Contact
          support@textual.ai for assistance
        </h1>
      )}

      {/* Error Display */}
      {error && (
        <div className="tw-mb-6 tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-sm">
          <p className="tw-text-red-800 tw-font-medium">Error</p>
          <p className="tw-text-red-600">{error}</p>
        </div>
      )}

      {/* Search Results */}
      {result && (
        <div className="tw-mt-8 tw-grid tw-grid-cols-2 tw-gap-6">
          {/* Found IDs */}
          {result.products.length > 0 && (
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-4 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                <Icon name="check_circle" className="tw-text-green-600" />
                <h3 className="tw-text-lg tw-font-medium tw-text-green-600">
                  Found IDs
                </h3>
              </div>
              <div className="tw-max-h-64 tw-overflow-y-auto">
                <table className="tw-w-full tw-text-sm">
                  <thead className="tw-bg-gray-50 tw-sticky tw-top-0">
                    <tr>
                      <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                        InRiver ID
                      </th>
                      <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                        Product ID
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.products.map((product, index) => (
                      <tr
                        key={product.inriver_id}
                        className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 ${
                          index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50/50"
                        }`}
                      >
                        <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-gray-700">
                          {product.inriver_id}
                        </td>
                        <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-gray-700">
                          {product.external_id || product.product_id || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Not Found IDs */}
          {result.failed_ids.length > 0 && (
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-4 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                <Icon name="cancel" className="tw-text-red-600" />
                <h3 className="tw-text-lg tw-font-medium tw-text-red-600">
                  Not Found IDs
                </h3>
              </div>
              <div className="tw-max-h-64 tw-overflow-y-auto">
                <table className="tw-w-full tw-text-sm">
                  <thead className="tw-bg-gray-50 tw-sticky tw-top-0">
                    <tr>
                      <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                        InRiver ID
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.failed_ids.map((id, index) => (
                      <tr
                        key={id}
                        className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 ${
                          index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50/50"
                        }`}
                      >
                        <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-gray-600">
                          {id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Settings - Only show if we have results */}
      {result && result.products.length > 0 && (
        <div className="tw-border-t tw-border-gray-200 tw-pt-8">
          <h2 className="tw-text-xl tw-font-semibold tw-mb-4 tw-text-center">
            Upload Settings
          </h2>
          <UploadSettings customerSlug={customerSlug} onChange={setSettings} />
        </div>
      )}

      {/* Submit Button - Only show if we have results and settings */}
      {result && result.products.length > 0 && settings && (
        <div className="tw-flex tw-justify-center tw-mt-6 tw-border-t tw-border-gray-200 tw-pt-6">
          <UploadButton
            disabled={!canSubmit}
            submitting={submitting}
            readyText={`Import ${foundCount} Products`}
            submittingText="Importing..."
            actionUrl={getUploadWithPimUrl()}
            settings={settings}
            productIds={result.products.map((p) => p.inriver_id)}
            onSubmitStart={handleSubmitStart}
            onSubmitError={handleSubmitError}
          />
        </div>
      )}
    </div>
  );
};
